import React, { Component } from 'react';
import { connect } from 'react-redux';
import YouTubePlayer from 'react-player/lib/players/YouTube';
import { propTypes } from './props';
import uuidv4 from 'uuid/v4';
import { Mixpanel } from 'appSrc/components/Analytics/Mixpanel';
import { GoogleTagManager } from 'appSrc/components/Analytics/GoogleTagManager';
import { updateVars } from 'appSrc/redux/actions';
import style from './styles/style.module.css';

const SUPPORTED_PROPS = Object.keys( propTypes );


class DLYouTubePlayer extends Component {
    constructor( props ) {
        super( props );

        this.playerName = 'YouTube';
        this.player = {};
        this.hasStarted = false;
        this.startTime = 0;
        this.videoTitle = '';
        this.internalPlayer = {};
        this.id = `ytplayer-${ uuidv4() }`;
    }
    componentDidUpdate( prevProps ) {
        if( prevProps.url !== this.props.url ) {
            this.hasStarted = false;
            this.startTime = 0;
        }
        if( this.props.videoPlaying !== this.id ) {
            this.internalPlayer && this.internalPlayer.pauseVideo && this.internalPlayer.pauseVideo();
        }
    }
    componentWillUnmount() {
        if( this.autoplayCheck ) {
            window.clearTimeout( this.autoplayCheck );
        }
    }
    onReady = () => {
        this.internalPlayer = this.player.getInternalPlayer();
        this.videoTitle = this.internalPlayer.getVideoData().title;

        if( this.props.seekTo ) {
            this.player.seekTo( Math.ceil( this.props.seekTo / 1000 ) );
        }

        if( this.player.getDuration() === 0 ) {
            // if a video has a 0 duration, then it's actually live
            // this is different from the documentation, so it might not be true all the time
            this.player.live = true;
        }
        if( this.props.autoplay ) {
            // set a timeout to make sure the video is playing in .25 seconds
            this.autoplayCheck = window.setTimeout( () => {
                if( !this.hasStarted ) {
                    // autoplay failed, try to play again
                    this.internalPlayer.playVideo();
                    this.props.onError( {
                        type: 'autoplayerror',
                        error: 'YouTube Player has failed to autoplay',
                    } );
                }
                this.autoplayCheck = undefined;
            }, 1000 );
        }
        this.props.onReady( this.player );
    }
    onStart = () => {
        this.props.dispatch( updateVars( { videoPlaying: this.id } ) );
        this.props.onStart( this.player );
    }
    onPlay = () => {
        this.videoTitle = this.internalPlayer.getVideoData().title;
        // check to see if this is inital play
        if( this.hasStarted == false ) {
            this.hasStarted = true;
            if( this.props.autoplay ) {
                this.trackPlay( 'autoplay' );
            } else {
                this.trackPlay( 'started' );
            }
        } else {
            this.trackPlay( 'resumed' );
        }
        this.props.onPlay( this.player );
    }
    onProgress = () => {
        this.props.onProgress( this.player );
    }
    onDuration = () => {
        this.props.onDuration( this.player );
    }
    onPause = () => {
        var pauseTime = this.player.getCurrentTime();
        this.trackPause( pauseTime );
        this.props.onPause( this.player );
    }
    onBuffer = () => {
        this.props.onBuffer( this.player );
    }
    onBufferEnd = () => {
        this.props.onBufferEnd( this.player );
    }
    onSeek = () => {
        this.props.onSeek( this.player );
    }
    onEnded = () => {
        this.props.onEnded( this.player );
    }
    onError = () => {
        this.props.onError( this.player );
    }

    render() {
        const props = {
            ...this.props,
            playing: this.props.autoplay,
            height: '100%',
            width: '100%',
            config: {
                youtube: {
                    playerVars: {
                        autoplay: this.props.autoplay ? 1 : 0,
                        modestbranding: 1,
                        rel: 0,
                        enablejsapi: 1,
                        origin: process.env.REACT_APP_HOST,
                        cc_lang_pref: 'en',
                        cc_load_policy: 1,
                    },
                    preload: true,
                },
            },
            onReady: this.onReady,
            onStart: this.onStart,
            onPlay: this.onPlay,
            onProgress: this.onProgress,
            onDuration: this.onDuration,
            onPause: this.onPause,
            onBuffer: this.onBuffer,
            onBufferEnd: this.onBufferEnd,
            onSeek: this.onSeek,
            onEnded: this.onEnded,
            onError: this.onError,
        };
        const culledProps = Object.constrain( props, SUPPORTED_PROPS );

        return (
            <div className={ style.playerWrapper }>
                <YouTubePlayer className={ style.player } { ...culledProps }
                ref={ ( player ) => { this.player = player; } }/>
            </div>
        );
    }

    trackPlay = ( type ) => {
        try {
            this.startTime = this.player.getCurrentTime();
            let gEventName, mixEventName;
            switch ( type ) {
                case 'autoplay':
                    gEventName = 'Video Autoplay';
                    mixEventName = 'videoAutoplay';
                    break;
                case 'started':
                    gEventName = 'Video Started';
                    mixEventName = 'videoStarted';
                    break;
                default:
                    gEventName = 'Video Resumed';
                    mixEventName = 'videoResumed';
            }
            GoogleTagManager.track( gEventName, {
                video_provider: 'youtube',
                video_provider_link: this.props.videoId,
                video_title: this.videoTitle,
                attached_to: this.props.attachmentType,
                attached_id: this.props.attachmentId,
                video_author_id: this.props.authorId,
                start_time: this.startTime,
                resuming: this.hasPaused,
                ...this.props.metaData,
            } );
            Mixpanel.track( mixEventName, {
                video_provider: 'youtube',
                video_provider_link: this.props.videoId,
                video_title: this.videoTitle,
                attached_to: this.props.attachmentType,
                attached_id: this.props.attachmentId,
                video_author_id: this.props.authorId,
                start_time: this.startTime,
                resuming: this.hasPaused,
                ...this.props.metaData,
            } );
        } catch (e) {
            console.error( 'Analytics error', e );
        }
    }
    trackPause = ( time ) => {
        try {
            var pauseTime = time;
            GoogleTagManager.track( 'Video Paused', {
                video_provider: 'youtube',
                video_provider_id: this.props.videoId,
                video_title: this.videoTitle,
                attached_to: this.props.attachmentType,
                attached_id: this.props.attachmentId,
                video_author_id: this.props.authorId,
                start_time: this.startTime,
                pause_time: pauseTime,
                ...this.props.metaData,
            } );
            Mixpanel.track( 'videoPaused', {
                video_provider: 'youtube',
                video_provider_id: this.props.videoId,
                video_title: this.videoTitle,
                attached_to: this.props.attachmentType,
                attached_id: this.props.attachmentId,
                video_author_id: this.props.authorId,
                start_time: this.startTime,
                pause_time: pauseTime,
                ...this.props.metaData,
            } );
            this.startTime = pauseTime;
        } catch (e) {
            console.error( 'Analytics error', e );
        }
    }
}
DLYouTubePlayer.playerName = 'YouTube';
DLYouTubePlayer.canPlay = YouTubePlayer.canPlay;
DLYouTubePlayer.defaultProps = {
    onReady: () => {},
    onStart: () => {},
    onPlay: () => {},
    onProgress: () => {},
    onDuration: () => {},
    onPause: () => {},
    onBuffer: () => {},
    onBufferEnd: () => {},
    onSeek: () => {},
    onEnded: () => {},
    onError: () => {},
    muted: false,
    autoplay: false,
};

const mapStateToProps = ( state ) => {
    return {
        videoPlaying: state.vars.videoPlaying,
    };
};

export default connect( mapStateToProps )( DLYouTubePlayer );
